'use client'

import { cn } from '@/lib/utils'
import { useStoreI18n } from '@/store'
import { getWeatherAltText } from '@/utils'
import dynamic from 'next/dynamic'
import Image from 'next/image'
import { useMemo } from 'react'

interface Props {
  icon?: string
  className?: string
  alt?: string
  width?: number
  height?: number
}

const IconComponent = ({
  icon,
  className,
  alt,
  width = 96,
  height = 96
}: Props) => {
  const currentLocale = useStoreI18n.use.currentLocale()

  const isWeatherIcon = !!icon?.startsWith('weather_big_')
  const weatherIconSrc = isWeatherIcon
    ? `/public/weather_icons/${icon!.replace('weather_big_', '')}-weather-CLR-filled@3x.png`
    : ''

  const DynamicIcon = useMemo(() => {
    if (isWeatherIcon || !icon) {
      return null
    }
    return dynamic(
      () => import(`../../../../../public/public/icons/${icon}.svg`),
      { ssr: false }
    )
  }, [icon, isWeatherIcon])

  const altText = useMemo(() => {
    if (alt !== undefined) {
      return alt
    }
    if (!icon) {
      return ''
    }

    if (isWeatherIcon) {
      return getWeatherAltText(icon, currentLocale)
    }

    return ''
  }, [alt, icon, isWeatherIcon, currentLocale])

  const isDecorative = !altText

  return (
    <div
      className={cn('[&_svg]:size-full', className)}
      role={isDecorative ? undefined : 'img'}
      aria-label={isDecorative ? undefined : altText}
      aria-hidden={isDecorative ? true : undefined}
    >
      {isWeatherIcon ? (
        <Image
          src={weatherIconSrc}
          width={width}
          height={height}
          alt={altText}
        />
      ) : DynamicIcon ? (
        <DynamicIcon />
      ) : null}
    </div>
  )
}

export default IconComponent
